import { EntityAdapter, Update } from "@reduxjs/toolkit";
import { ContouringCustomizationEntities, ContouringCustomizationOutput, GlobalContouringRoi } from "../../contouring/contouring-types";
import { DoseCustomizationEntities, DoseCustomizationOutput } from "../../dose/dose-types";
import { ImageCustomizationEntities, ImageCustomizationOutput } from "../../image/image-types";
import { AdaptCustomizationEntities, AdaptCustomizationOutput } from "../../adapt/adapt-types";
import { AeTitleRule, CustomizationBase, CustomizationObjectType, CustomizationOutput, DEFAULT_MODEL_CUSTOMIZATION_NAME, DicomAttributeRule, DicomRule, METADATA_FILENAME_ATTRIBUTE, Modality, ModalityDirection, Model, ModelVisibility, OutputMetadataItem } from "../customization-types";
import { AnySliceState, convertBackendValidationErrorToViewModels, setObjectAndAncestorsAsModified } from "../reducer-helpers";
import { BackendValidationErrorViewModel } from "../store-errors";
import { assertIsDefined } from "../../../util/assert";
import { get, pull } from "lodash-es";
import { notEmpty } from "../../../util/filter";
import { BackendValidationError } from "../../../util/errors";


type OutputAdapterType = EntityAdapter<ContouringCustomizationOutput, string> | EntityAdapter<DoseCustomizationOutput, string> |
    EntityAdapter<ImageCustomizationOutput, string> | EntityAdapter<AdaptCustomizationOutput, string>;

type CustomizationEntities = ContouringCustomizationEntities | DoseCustomizationEntities | ImageCustomizationEntities | AdaptCustomizationEntities;


export type RemovedContourOutputEntities = {
    globalRoiChanges?: Update<GlobalContouringRoi, string>[];
    roiIdsToBeRemoved?: string[];
};

export type RemovedDoseOutputEntities = {
    roiIdsToBeRemoved?: string[];
    targetIdsToBeRemoved?: string[];
};

export type RemovedImageOutputEntities = {
    dicomRestrictionIdsToBeRemoved?: string[];
    outputGeometryIdsToBeRemoved?: string[];
    contourGenerationIdsToBeRemoved?: string[];
    postProcessingIdsToBeRemoved?: string[];
    dicomTagIdsToBeRemoved?: string[];
    keepLargestComponentIdsToBeRemoved?: string[];
    fillHolesIdsToBeRemoved?: string[];
    bodyMaskIdsToBeRemoved?: string[];
    holeMaskIdsToBeRemoved?: string[];
}

export type RemovedAdaptOutputEntities = {
    adaptRoiRuleIdsToBeRemoved?: string[];
}

/** Collection of slice-specific IDs for removal (where appropriate) */
export type RemovedOutputEntities = RemovedContourOutputEntities & RemovedDoseOutputEntities & RemovedImageOutputEntities & RemovedAdaptOutputEntities & {
    metadataIdsToBeRemoved?: string[];
    allRemovedIds: string[];
};

/**
 * This class contains generic reducer methods for different model types (contour+, dose+, etc). These should be
 * called from within appropriate slices (e.g. contouringSlice) to ensure the slices run similar code. Use the
 * appropriate inherited class for your slice (ContourSliceHelper, DoseSliceHelper, etc).
 */
export default abstract class GenericSliceWrapper<T extends AnySliceState> {

    modelAdapter: EntityAdapter<Model, string>;
    customizationBaseAdapter: EntityAdapter<CustomizationBase, string>;
    abstract outputAdapter: OutputAdapterType;
    outputMetadataAdapter: EntityAdapter<OutputMetadataItem, string>;
    aeTitleRuleAdapter: EntityAdapter<AeTitleRule, string>;
    dicomRuleAdapter: EntityAdapter<DicomRule, string>;
    dicomAttributeRuleAdapter: EntityAdapter<DicomAttributeRule, string>;
    backendValidationErrorAdapter: EntityAdapter<BackendValidationErrorViewModel, string>;


    constructor(
        modelAdapter: EntityAdapter<Model, string>,
        customizationBaseAdapter: EntityAdapter<CustomizationBase, string>,
        outputMetadataAdapter: EntityAdapter<OutputMetadataItem, string>,
        aeTitleRuleAdapter: EntityAdapter<AeTitleRule, string>,
        dicomRuleAdapter: EntityAdapter<DicomRule, string>,
        dicomAttributeRuleAdapter: EntityAdapter<DicomAttributeRule, string>,
        backendValidationErrorAdapter: EntityAdapter<BackendValidationErrorViewModel, string>,
    ) {
        this.modelAdapter = modelAdapter;
        this.customizationBaseAdapter = customizationBaseAdapter;
        this.outputMetadataAdapter = outputMetadataAdapter;
        this.aeTitleRuleAdapter = aeTitleRuleAdapter;
        this.dicomRuleAdapter = dicomRuleAdapter;
        this.dicomAttributeRuleAdapter = dicomAttributeRuleAdapter;
        this.backendValidationErrorAdapter = backendValidationErrorAdapter;
    }

    /**
     * Updates a model's visibility.
     * @param modelId Internal (entity adapter) ID of the model to update.
     * @param visibility The new visibility setting for the model.
     */
    public updateModelVisibility(state: T, modelId: string, visibility: ModelVisibility) {
        this.modelAdapter.updateOne(state.models, { id: modelId, changes: { visibility: visibility, isModified: true } });
    }

    /**
    * Updates the description field on a customization base object.
    * @param customizationBaseId Internal (entity adapter) ID of the customization base to update.
    * @param description The new description to set.
    */
    public updateModelCustomizationDescription(state: T, customizationBaseId: string, description: string) {
        this.customizationBaseAdapter.updateOne(state.customizationBases, {
            id: customizationBaseId,
            changes: { description, isModified: true },
        });
        setObjectAndAncestorsAsModified(state, [customizationBaseId], CustomizationObjectType.CustomizationBase);
    }

    /**
     * Updates a customization metadata item tuple. If either attribute or value is left as undefined then that particular prop is not updated
     * and the existing prop is left unchanged.
     * @param metadataId Internal (entity adapter) ID of the customization metadata item that will be updated.
     * @param attribute The new attribute name of the metadata item that will be used, or undefined if attribute should not be changed.
     * @param value The new value of the metadata item, or undefined if value should not be changed.
     * @param isUndoOperation Marks this update as part of an undo operation. Note that the correct reverted attribute and value props
     * must be supplied to this function manually, this function does not perform an undo by itself. Optional, defaults to false.
     */
    public updateMetadataItem(state: T, metadataId: string, attribute: string | undefined, value: string | undefined, isUndoOperation?: boolean) {
        const metadataItem = state.outputMetadata.entities[metadataId];
        if (metadataItem && (attribute !== undefined || value !== undefined)) {
            const changes: Partial<OutputMetadataItem> = { isModified: !isUndoOperation };
            if (attribute !== undefined) { changes['attribute'] = attribute; }
            if (value !== undefined) { changes['value'] = value; }

            this.outputMetadataAdapter.updateOne(state.outputMetadata, { id: metadataId, changes: changes });

            // update matching output filename prop if needed
            if (metadataItem.modelCustomizationOutputId !== undefined) {
                const currentAttribute = attribute !== undefined ? attribute : metadataItem.attribute;
                const currentValue = value !== undefined ? value : metadataItem.value;

                if (currentAttribute === METADATA_FILENAME_ATTRIBUTE) {
                    // update changed filename
                    this.updateOutput(state, { id: metadataItem.modelCustomizationOutputId, changes: { filename: currentValue || '' } });
                } else if (metadataItem.attribute === METADATA_FILENAME_ATTRIBUTE) {
                    // attribute name was changed away from 'filename' -- try to find another matching value, then update
                    const output = this.findOutput(state, metadataItem.modelCustomizationOutputId);
                    if (output === undefined) { throw new Error(`Could not find customization output matching ID ${metadataItem.modelCustomizationOutputId}`); }
                    const matchingFilenameMetadataItems = Object.values(state.outputMetadata.entities)
                        .filter(m => m !== undefined && output.metadata.includes(m.id) && m.attribute === METADATA_FILENAME_ATTRIBUTE);
                    this.updateOutput(state, { id: metadataItem.modelCustomizationOutputId, changes: { filename: matchingFilenameMetadataItems[0]?.value || '' } });

                }
            }

            if (!isUndoOperation) {
                setObjectAndAncestorsAsModified(state, [metadataId], CustomizationObjectType.Metadata);
            }
        }
    }

    /**
     * Removes a customization metadata item.
     * @param metadataId Internal (entity adapter) ID of the customization metadata item that will be removed.
     */
    public removeMetadataItem(state: T, metadataId: string) {
        const metadataItem = state.outputMetadata.entities[metadataId];
        if (metadataItem) {
            const output = metadataItem.modelCustomizationOutputId ? this.findOutput(state, metadataItem.modelCustomizationOutputId) : undefined;

            this.outputMetadataAdapter.removeOne(state.outputMetadata, metadataId);

            this.backendValidationErrorAdapter.removeOne(state.backendValidationErrors, metadataId);

            if (output) {
                this.updateOutput(state, { id: output.id, changes: { metadata: output.metadata.filter(mId => mId !== metadataId), isModified: true } });

                // update matching output filename prop if needed -- we might have multiple filename metadata entries so first try finding another match
                const matchingFilenameMetadataItems = Object.values(state.outputMetadata.entities)
                    .filter(m => m !== undefined && output.metadata.includes(m.id) && m.attribute === METADATA_FILENAME_ATTRIBUTE);
                this.updateOutput(state, { id: output.id, changes: { filename: matchingFilenameMetadataItems[0]?.value || '' } });

                setObjectAndAncestorsAsModified(state, [output.id], CustomizationObjectType.CustomizationOutput);
            }
        }
    }

    /**
    * Adds supplied AE Title rule into customization configuration.
    * @param aeTitleRule The AE Title rule to add.
    */
    public addAeTitleRule(state: T, aeTitleRule: AeTitleRule) {
        assertIsDefined(aeTitleRule.modelCustomizationBaseId, 'modelCustomizationBaseId not defined for new AE Title rule');

        const customization = state.customizationBases.entities[aeTitleRule.modelCustomizationBaseId];
        assertIsDefined(customization, `Could not find customization base with matching ID ${aeTitleRule.modelCustomizationBaseId}`);

        this.aeTitleRuleAdapter.addOne(state.aeTitleRules, aeTitleRule);
        this.customizationBaseAdapter.updateOne(state.customizationBases, { id: customization.id, changes: { aeTitleRules: customization.aeTitleRules.concat(aeTitleRule.id), isModified: true } });
        setObjectAndAncestorsAsModified(state, [aeTitleRule.id], CustomizationObjectType.AeTitleRule);
    }

    /**
     * Updates the 'action' field in an AE Title rule.
     * @param aeTitleRuleId Internal (entity adapter) ID of the AE Title rule to update.
     * @param action The new action value for the rule.
     */
    public updateAeTitleRuleAction(state: T, aeTitleRuleId: string, action: string) {
        this.aeTitleRuleAdapter.updateOne(state.aeTitleRules, { id: aeTitleRuleId, changes: { action: action, isModified: true } });

        setObjectAndAncestorsAsModified(state, [aeTitleRuleId], CustomizationObjectType.AeTitleRule);
    }

    /**
     * Removes an AE Title rule from customization configuration.
     * Throws if the AE Title rule to be removed is not found from configuration.
     * @param aeTitleRuleId Internal (entity adapter) ID of the AE Title rule to remove.
     */
    public removeAeTitleRule(state: T, aeTitleRuleId: string) {
        const aeTitleRuleToRemove = state.aeTitleRules.entities[aeTitleRuleId];
        this.aeTitleRuleAdapter.removeOne(state.aeTitleRules, aeTitleRuleId);

        // update matching parent objects
        assertIsDefined(aeTitleRuleToRemove, 'AE title rule about to be removed is not defined');
        assertIsDefined(aeTitleRuleToRemove.modelCustomizationBaseId, 'modelCustomizationBaseId not defined for the AE title rule about to be removed');
        const customization = state.customizationBases.entities[aeTitleRuleToRemove.modelCustomizationBaseId];
        assertIsDefined(customization, `Could not find customization base with matching ID ${aeTitleRuleToRemove.modelCustomizationBaseId}`);
        this.customizationBaseAdapter.updateOne(state.customizationBases, { id: customization.id, changes: { aeTitleRules: pull(customization.aeTitleRules, aeTitleRuleId), isModified: true } });
        setObjectAndAncestorsAsModified(state, [customization.id], CustomizationObjectType.CustomizationBase);
    }

    /**
     * Adds supplied DICOM rule into customization configuration.
     * Note that DICOM attribute rules under a DICOM rule must be added separately.
     * @param dicomRule The DICOM rule to add.
     */
    public addDicomRule(state: T, dicomRule: DicomRule) {
        assertIsDefined(dicomRule.modelCustomizationBaseId, 'modelCustomizationBaseId not defined for new DICOM rule');

        const customization = state.customizationBases.entities[dicomRule.modelCustomizationBaseId];
        assertIsDefined(customization, `Could not find customization base with matching ID ${dicomRule.modelCustomizationBaseId}`);

        this.dicomRuleAdapter.addOne(state.dicomRules, dicomRule);
        this.customizationBaseAdapter.updateOne(state.customizationBases, { id: customization.id, changes: { dicomRules: customization.dicomRules.concat(dicomRule.id), isModified: true } });
        setObjectAndAncestorsAsModified(state, [dicomRule.id], CustomizationObjectType.DicomRule);
    }

    /**
     * Removes a DICOM rule from customization configuration. Also removes any DICOM attribute
     * rules under the DICOM rule.
     * Throws if the DICOM rule to be removed is not found from configuration.
     * @param dicomRuleId Internal (entity adapter) ID of the DICOM rule to remove.
     */
    public removeDicomRule(state: T, dicomRuleId: string) {
        const dicomRuleToRemove = state.dicomRules.entities[dicomRuleId];
        if (dicomRuleToRemove === undefined) { throw new Error(`Could not find DICOM rule ${dicomRuleId} to remove`); }
        const removedAttributeIds = [...dicomRuleToRemove.dicomAttributes];
        this.dicomAttributeRuleAdapter.removeMany(state.dicomAttributeRules, removedAttributeIds);
        this.dicomRuleAdapter.removeOne(state.dicomRules, dicomRuleId);

        // update matching parent objects
        assertIsDefined(dicomRuleToRemove, 'DICOM rule about to be removed is not defined');
        assertIsDefined(dicomRuleToRemove.modelCustomizationBaseId, 'modelCustomizationBaseId not defined for the DICOM rule about to be removed');
        const customization = state.customizationBases.entities[dicomRuleToRemove.modelCustomizationBaseId];
        assertIsDefined(customization, `Could not find customization base with matching ID ${dicomRuleToRemove.modelCustomizationBaseId}`);
        this.customizationBaseAdapter.updateOne(state.customizationBases, { id: customization.id, changes: { dicomRules: pull(customization.dicomRules, dicomRuleId), isModified: true } });
        setObjectAndAncestorsAsModified(state, [customization.id], CustomizationObjectType.CustomizationBase);
    }

    /**
     * Adds supplied DICOM attribute rule into customization configuration.
     * Note that the parent DICOM rule must already exist in configuration or this function will throw.
     * @param dicomAttributeRule The DICOM attribute rule to add.
     */
    public addDicomAttributeRule(state: T, dicomAttributeRule: DicomAttributeRule) {
        const parentDicomRule = state.dicomRules.entities[dicomAttributeRule.parentDicomRuleId];
        if (parentDicomRule === undefined) { throw new Error(`Could not find parent DicomRule object with ID ${dicomAttributeRule.parentDicomRuleId}`); }
        if (!parentDicomRule.dicomAttributes.includes(dicomAttributeRule.id)) {
            this.dicomRuleAdapter.updateOne(state.dicomRules, {
                id: dicomAttributeRule.parentDicomRuleId,
                changes: {
                    dicomAttributes: parentDicomRule.dicomAttributes.concat(dicomAttributeRule.id),
                    isModified: true
                }
            });
        }

        this.dicomAttributeRuleAdapter.addOne(state.dicomAttributeRules, dicomAttributeRule);

        // mark matching parent entities as modified
        setObjectAndAncestorsAsModified(state, [dicomAttributeRule.id], CustomizationObjectType.DicomAttributeRule);
    }

    /**
     * Removes a DICOM attribute rule from customization configuration.
     * Throws if the DICOM attribute rule to be removed or its parent DICOM rule are not found from configuration.
     * @param dicomAttributeRuleId Internal (entity adapter) ID of the DICOM attribute rule to remove.
     */
    public removeDicomAttributeRule(state: T, dicomAttributeRuleId: string) {
        const dicomAttributeToRemove = state.dicomAttributeRules.entities[dicomAttributeRuleId];
        if (dicomAttributeToRemove === undefined) { throw new Error(`Could not find DICOM attribute rule ${dicomAttributeRuleId} to remove`); }

        const parentDicomRule = state.dicomRules.entities[dicomAttributeToRemove.parentDicomRuleId];
        if (parentDicomRule === undefined) { throw new Error(`Could not find parent DICOM rule ${dicomAttributeToRemove.parentDicomRuleId} for DICOM attribute ${dicomAttributeRuleId} -- cannot remove attribute`); }
        this.dicomRuleAdapter.updateOne(state.dicomRules, {
            id: dicomAttributeToRemove.parentDicomRuleId,
            changes: {
                dicomAttributes: pull(parentDicomRule.dicomAttributes, dicomAttributeRuleId),
                isModified: true
            }
        });

        this.dicomAttributeRuleAdapter.removeOne(state.dicomAttributeRules, dicomAttributeRuleId);

        setObjectAndAncestorsAsModified(state, [parentDicomRule.id], CustomizationObjectType.DicomRule);
    }

    /**
     * Updates a DICOM attribute rule.
     * @param dicomAttributeRuleId Internal (entity adapter) ID of the DICOM attribute rule to update.
     * @param dicomAttribute The attribute field of the rule is updated to this value.
     * @param dicomValue The DICOM value field of the rule is updated to this value.
     */
    updateDicomAttributeRule(state: T, dicomAttributeRuleId: string, dicomAttribute: string, dicomValue: string) {
        const dicomAttributeRule = state.dicomAttributeRules.entities[dicomAttributeRuleId];
        if (dicomAttributeRule === undefined) { throw new Error(`Could not find DICOM attribute rule with id ${dicomAttributeRuleId}`); }

        this.dicomAttributeRuleAdapter.updateOne(state.dicomAttributeRules,
            {
                id: dicomAttributeRuleId,
                changes: {
                    attribute: dicomAttribute,
                    value: dicomValue,
                    isModified: true,
                }
            });

        setObjectAndAncestorsAsModified(state, [dicomAttributeRuleId], CustomizationObjectType.DicomAttributeRule);
    }


    /**
     * Removes a tag from a (newly created) customization metadata item that would mark it as being the target of being
     * automatically scrolled to in UI.
     * 
     * This tag is generally given to metadata items after their creation in the UI and should be removed once
     * the UI has scrolled to the correct position in the UI once.
     * 
     * @param metadataId Internal (entity adapter) ID of the customization metadata item where this tag is to be removed.
     */
    public removeScrollToViewFromMetadata(state: T, metadataId: string) {
        this.outputMetadataAdapter.updateOne(state.outputMetadata, { id: metadataId, changes: { scrollToView: false } });
    }

    /**
     * Removes the specified customization base entity and all entities under it in the hierarchy.
     * @param customizationBaseId The ID of the customization base to remove.
     */
    public removeCustomizationBase(state: T, customizationBaseId: string) {
        const customization = state.customizationBases.entities[customizationBaseId];

        if (customization === undefined) {
            throw new Error(`Could not find customization base ${customizationBaseId}`);
        }

        if (customization.customizationName === DEFAULT_MODEL_CUSTOMIZATION_NAME) {
            throw new Error('Default customizations cannot be deleted.');
        }

        // mark base model as modified so we know we need to save the configuration
        setObjectAndAncestorsAsModified(state, [customizationBaseId], CustomizationObjectType.CustomizationBase);

        const outputs = customization.outputs.map(oId => this.findOutput(state, oId)).filter(notEmpty);
        const dicomAttributeIdsToBeRemoved: string[] = [];

        // get dicom attribute rules to remove
        for (const dicomRuleId of customization.dicomRules) {
            const dicomRule = state.dicomRules.entities[dicomRuleId];
            if (dicomRule === undefined) {
                throw new Error(`Could not retrieve dicom rule ${dicomRuleId} for customization base ${customizationBaseId}`);
            }
            dicomAttributeIdsToBeRemoved.push(...dicomRule.dicomAttributes);
        }

        const model = state.models.entities[customization.modelId];
        if (model === undefined) {
            throw new Error(`Could not retrieve model parent ${customization.modelId} for customization base ${customizationBaseId}`);
        }

        let removedOutputEntities: RemovedOutputEntities | undefined;
        outputs.forEach(output => {
            const entitiesForRemoval = this.collectCustomizationOutputEntitiesFromStoreForRemoval(state, output, customizationBaseId);
            if (!removedOutputEntities) {
                removedOutputEntities = entitiesForRemoval;
            } else {
                removedOutputEntities = Object.assign({}, removedOutputEntities, entitiesForRemoval);
            }
        });

        // remove & handle removal of slice-specific items (including updating global rois where relevant)
        if (removedOutputEntities) {
            this.removeOutputRelatedItems(state, customization.outputs, removedOutputEntities);
        }

        // delete generic items
        this.dicomAttributeRuleAdapter.removeMany(state.dicomAttributeRules, dicomAttributeIdsToBeRemoved);
        this.aeTitleRuleAdapter.removeMany(state.aeTitleRules, customization.aeTitleRules);
        this.dicomRuleAdapter.removeMany(state.dicomRules, customization.dicomRules);
        this.customizationBaseAdapter.removeOne(state.customizationBases, customizationBaseId);

        // update model
        this.modelAdapter.updateOne(state.models, { id: customization.modelId, changes: { customizations: model.customizations.filter(c => c !== customizationBaseId) } });

        // no need to specifically remove BACKEND validation errors here -- they will not show up in UI and will be cleared from store during next save operation.
        // form validation errors are derived from current entity state and need no explicit cleanup.
    }

    /**
     * Removes the specified customization output entity and all entities under it in the hierarchy.
     * @param customizationOutputId The ID of the customization output to remove.
     */
    public removeCustomizationOutput(state: T, customizationOutputId: string) {
        const output = this.findOutput(state, customizationOutputId);
        if (output === undefined) {
            throw new Error(`Could not find customization output ${customizationOutputId}`);
        }

        const customizationBase = state.customizationBases.entities[output.modelCustomizationBaseId];
        if (customizationBase === undefined) {
            throw new Error(`Could not find customization base ${output.modelCustomizationBaseId} for output ${customizationOutputId}`);
        }

        const entitiesForRemoval = this.collectCustomizationOutputEntitiesFromStoreForRemoval(state, output, customizationBase.id);

        this.removeOutputRelatedItems(state, [customizationOutputId], entitiesForRemoval);

        // update customization base
        this.customizationBaseAdapter.updateOne(state.customizationBases, { id: customizationBase.id, changes: { outputs: customizationBase.outputs.filter(o => o !== customizationOutputId) } });

        // no need to specifically remove BACKEND validation errors here -- they will not show up in UI and will be cleared from store during next save operation.
        // form validation errors are derived from current entity state and need no explicit cleanup.
    }

    /**
     * Marks any supplied customization entities as having been modified.
     * @param action A collection of customization entities to mark as modified as per their
     * entity adapter IDs.
     */
    public setStateObjectsAsModified(state: T, entitiesToUpdate: Partial<CustomizationEntities>) {

        // first set all supplied common items as modified

        const aeTitleRuleIds = entitiesToUpdate.aeTitleRules?.map(id => id.id);
        const dicomRuleIds = entitiesToUpdate.dicomRules?.map(id => id.id);
        const dicomAttributeRuleIds = entitiesToUpdate.dicomAttributeRules?.map(id => id.id);
        const modelsIds = entitiesToUpdate.models?.map(id => id.id);
        const customizationBasesIds = entitiesToUpdate.customizationBases?.map(id => id.id);

        if (aeTitleRuleIds && aeTitleRuleIds.length > 0) {
            this.aeTitleRuleAdapter.updateMany(
                state.aeTitleRules,
                aeTitleRuleIds.map(aeTitleRuleId => ({ id: aeTitleRuleId, changes: { isModified: true } }))
            );
        }

        if (dicomRuleIds && dicomRuleIds.length > 0) {
            this.dicomRuleAdapter.updateMany(
                state.dicomRules,
                dicomRuleIds.map(dicomRuleId => ({ id: dicomRuleId, changes: { isModified: true } }))
            );
        }

        if (dicomAttributeRuleIds && dicomAttributeRuleIds.length > 0) {
            this.dicomAttributeRuleAdapter.updateMany(
                state.dicomAttributeRules,
                dicomAttributeRuleIds.map(dicomAttributeRuleId => ({ id: dicomAttributeRuleId, changes: { isModified: true } }))
            );
        }

        if (modelsIds && modelsIds.length > 0) {
            this.modelAdapter.updateMany(
                state.models,
                modelsIds.map(modelId => ({ id: modelId, changes: { isModified: true } }))
            );
        }

        if (customizationBasesIds && customizationBasesIds.length > 0) {
            this.customizationBaseAdapter.updateMany(
                state.customizationBases,
                customizationBasesIds.map(customizationBaseId => ({ id: customizationBaseId, changes: { isModified: true } }))
            );
        }


        // then set specific items as modified
        this.setOutputRelatedItemsAsModified(state, entitiesToUpdate);

    }

    /**
     * Toggles a tag on or off from active tag filters.
     * @param tag label of the tag to toggle.
     */
    public toggleActiveTagFilter(state: T, tag: string) {
        const currentTagFilters = state.activeTagFilters;

        // if tag is already active, remove it
        if (currentTagFilters.includes(tag)) {
            state.activeTagFilters = currentTagFilters.filter(t => t !== tag);
        } else {
            // otherwise add it
            state.activeTagFilters.push(tag);
        }
    }


    /**
     * Resets current active tag filter set to an empty array.
     */
    public resetActiveTagFilters(state: T) {
        state.activeTagFilters = [];
    }


    /**
     * Toggles a tag on or off from active tag filters.
     * @param tag label of the tag to toggle.
     */
    public toggleActiveVersionTagFilter(state: T, tag: string) {
        const currentTagFilters = state.activeVersionTags;

        // if tag is already active, remove it
        if (currentTagFilters.includes(tag)) {
            state.activeVersionTags = currentTagFilters.filter(t => t !== tag);
        } else {
            // otherwise add it
            state.activeVersionTags.push(tag);
        }
    }  
    
    /**
     * Resets current active tag filter set to an empty array.
     */
    public resetActiveVersionTagFilters(state: T) {
        state.activeVersionTags = [];
    }


    /** Toggles a modality on or off from active modality filters for the given direction (input/output) and modality. */
    public toggleActiveModalityFilter(state: T, direction: ModalityDirection, modality: Modality) {
        if (direction === ModalityDirection.Input) {
            const currentModalityFilters = state.activeInputModalityFilters;

            // if modality is already active, remove it
            if (currentModalityFilters.includes(modality)) {
                state.activeInputModalityFilters = currentModalityFilters.filter(m => m !== modality);
            } else {
                // otherwise add it
                state.activeInputModalityFilters.push(modality);
            }
        } else if (direction === ModalityDirection.Output) {
            const currentModalityFilters = state.activeOutputModalityFilters;

            // if modality is already active, remove it
            if (currentModalityFilters.includes(modality)) {
                state.activeOutputModalityFilters = currentModalityFilters.filter(m => m !== modality);
            } else {
                // otherwise add it
                state.activeOutputModalityFilters.push(modality);
            }
        } else {
            throw new Error(`Unknown modality direction: ${direction}`);
        }
    }

    /** Resets (unselects) all current active modality filters for the given direction (input/output). */
    public resetActiveModalityFilters(state: T, direction: ModalityDirection) {
        if (direction === ModalityDirection.Input) {
            state.activeInputModalityFilters = [];
        } else if (direction === ModalityDirection.Output) {
            state.activeOutputModalityFilters = [];
        } else {
            throw new Error(`Unknown modality direction: ${direction}`);
        }
    }

    /** Sets the active free-text filter value. */
    public setActiveFreeTextFilter(state: T, value: string) {
        state.activeFreeTextFilter = value;
    }

    /** Resets the active free-text filter to an empty string. */
    public resetActiveFreeTextFilter(state: T) {
        state.activeFreeTextFilter = '';
    }

    /** Sets whether deprecated models are shown. */
    public setAreDeprecatedModelsShown(state: T, areShown: boolean) {
        state.areDeprecatedModelsShown = areShown;
    }

    /** Sets whether non-licensed models are shown. */
    public setAreNonLicensedModelsShown(state: T, areShown: boolean) {
        state.areNonLicensedModelsShown = areShown;
    }

    /** Sets whether user-hidden models are shown. */
    public setAreUserHiddenModelsShown(state: T, areShown: boolean) {
        state.areUserHiddenModelsShown = areShown;
    }

    /**
     * Marks model customization reset as finished. 
     * @param resetWasSuccessful True if reset was successful, false otherwise.
     * @param errorMessage Optional error message if output reset failed.
     */
    public finishResetAllModelCustomizations(state: T, resetWasSuccessful: boolean, errorMessage?: string) {
        state.isAllModelCustomizationsResetInProgress = false;
        state.allModelCustomizationsResetError = errorMessage || null;

        // clear any save errors if reset was successful as resetting also saves
        if (resetWasSuccessful) {
            this.clearSaveErrors(state);
        }
    }



    /****  abstract model type-specific getters etc   ****/

    /** Returns customization output from an entity adapter store of matching slice. */
    protected abstract findOutput(state: T, outputId: string): CustomizationOutput | undefined;

    /** Updates a customization output from an entity adapter store of matching slice. */
    protected abstract updateOutput(state: T, update: Update<CustomizationOutput, string>): void;

    /** Collects which store entities need to be removed and updated when a customization output is removed. */
    public abstract collectCustomizationOutputEntitiesFromStoreForRemoval(state: T, output: CustomizationOutput, customizationBaseId: string): RemovedOutputEntities;

    protected abstract removeOutputRelatedItems(state: T, outputIds: string[], items: RemovedOutputEntities): void;

    protected abstract setOutputRelatedItemsAsModified(state: T, entitiesToUpdate: Partial<CustomizationEntities>): void;


    /** Clears all save-related error state. Call this when loading fresh data or reverting changes. */
    public clearSaveErrors(state: T) {
        state.modelCustomizationSaveError = null;
        state.concurrentSaveError = null;
    }

    public collectSaveErrors(state: T, saveWasSuccessful: boolean, backendValidationError?: BackendValidationError) {
        try {
            if (!saveWasSuccessful && backendValidationError !== undefined) {
                const backendValidationErrors = convertBackendValidationErrorToViewModels(state, backendValidationError);
                if (backendValidationErrors.length > 0) {
                    // apply new validation errors and override whatever error message we received from sagas
                    this.backendValidationErrorAdapter.addMany(state.backendValidationErrors, backendValidationErrors);

                    // display a generic error message in the UI if we have collected all validation errors successfully,
                    // otherwise (if we have any 'NONE' customization object types) just dump out the error message we 
                    // received from the backend
                    if (backendValidationErrors.some(e => e.type === CustomizationObjectType.None)) {
                        const collectedErrorMessages = backendValidationErrors.map(e => e.message).join('. ');
                        state.modelCustomizationSaveError = 'Could not save customizations because they have invalid entries. Please fix any validation errors and try again: '
                            + collectedErrorMessages + '.';
                    } else {
                        state.modelCustomizationSaveError = 'Could not save customizations because they have invalid entries. Please fix any validation errors and try again.';
                    }
                }
            }
        }
        catch (ex) {
            const message: string | undefined = get(ex, 'message', undefined);
            state.modelCustomizationSaveError = `An error occurred when trying to parse validation error message${message ? `: ${message}` : '.'}`
        }
    }


}
